import Ionicons from "@expo/vector-icons/Ionicons";
import { useRouter } from "expo-router";
import { useMemo, useState } from "react";
import {
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from "react-native";

import { Modal } from "@/components/ui/app-modal";
import { useTranslation } from "react-i18next";

import { ThemedText } from "@/components/themed-text";
import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DateOfBirthPicker,
  maximumDateForMinimumAge,
  minimumDateForMaximumAge,
} from "@/components/ui/date-of-birth-picker";
import { Screen } from "@/components/ui/screen";
import { TextField } from "@/components/ui/text-field";
import { FRIENDSHIP_GOALS } from "@/constants/options";
import { Radius, Spacing } from "@/constants/theme";
import { useTheme } from "@/hooks/use-theme";
import { onboardingStrings as strings } from "@/i18n/features/onboarding.i18n";
import { referralStrings } from "@/i18n/features/referral.i18n";
import { ApiError, referralsApi } from "@/api";
import {
  MAX_DISPLAY_NAME_LENGTH,
  collapseDisplayName,
  generateDisplayName,
  validateDisplayName,
} from "@/lib/display-name";
import { useAuthStore } from "@/state/auth-store";

const MINIMUM_JOIN_AGE = 18;
const MAXIMUM_PROFILE_AGE = 120;
const MAX_HEADLINE_LENGTH = 50;
const MAX_CUSTOM_GOALS = 1;
const OTHER_GOAL_VALUE = "other";

function splitFriendshipGoals(value: string[] | null | undefined) {
  const optionValues = new Set(FRIENDSHIP_GOALS.map((goal) => goal.value));
  const selected: string[] = [];
  const custom: string[] = [];

  value?.filter(Boolean).forEach((goal) => {
    if (optionValues.has(goal)) {
      selected.push(goal);
    } else {
      custom.push(goal);
    }
  });

  return {
    selected: custom.length
      ? [OTHER_GOAL_VALUE]
      : selected.slice(0, 1),
    customValues: custom.slice(0, MAX_CUSTOM_GOALS),
  };
}

function normalizeFriendshipGoals(
  selected: string[],
  customValues: string[],
) {
  const goals = selected.filter((goal) => goal !== OTHER_GOAL_VALUE);

  return [
    ...goals,
    ...customValues.map((goal) => goal.trim()).filter(Boolean),
  ].slice(0, 1);
}

function mergeCustomGoals(current: string[], draft: string) {
  const optionValues = new Set(
    FRIENDSHIP_GOALS.flatMap((goal) => [
      goal.label.toLowerCase(),
      goal.value.toLowerCase(),
    ]),
  );
  const value = draft.trim();
  const next = [...current];

  if (
    value &&
    !optionValues.has(value.toLowerCase()) &&
    !next.some((goal) => goal.toLowerCase() === value.toLowerCase()) &&
    next.length < MAX_CUSTOM_GOALS
  ) {
    next.push(value);
  }

  return next.slice(0, MAX_CUSTOM_GOALS);
}

function ageFromDateOfBirth(value: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return null;
  }

  const dob = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(dob.getTime())) {
    return null;
  }

  const now = new Date();
  let age = now.getUTCFullYear() - dob.getUTCFullYear();
  const monthDelta = now.getUTCMonth() - dob.getUTCMonth();

  if (
    monthDelta < 0 ||
    (monthDelta === 0 && now.getUTCDate() < dob.getUTCDate())
  ) {
    age -= 1;
  }

  return age >= 0 ? age : null;
}

function ageBandFromDateOfBirth(value: string): string | null {
  const age = ageFromDateOfBirth(value);
  if (age === null) return null;

  if (age < 25) return "18-24";
  if (age >= 75) return "75+";

  const start = Math.floor(age / 5) * 5;
  return `${start}-${start + 4}`;
}

export default function ChooseName() {
  const router = useRouter();
  const { t } = useTranslation();
  const c = useTheme();
  const user = useAuthStore((s) => s.user);
  const setProfile = useAuthStore((s) => s.setProfile);
  const initialGoals = useMemo(
    () => splitFriendshipGoals(user?.lookingFor),
    [user?.lookingFor],
  );
  const initialDisplayName = useMemo(
    () =>
      user?.displayName?.trim() ||
      generateDisplayName(user?.id ?? user?.email ?? user?.avatarSeed),
    [user?.avatarSeed, user?.displayName, user?.email, user?.id],
  );

  const [name, setName] = useState(initialDisplayName);
  const [headline, setHeadline] = useState(user?.headline ?? "");
  const [dateOfBirth, setDateOfBirth] = useState(user?.dateOfBirth ?? "");
  const [friendshipGoals, setFriendshipGoals] = useState<string[]>(
    initialGoals.selected,
  );
  const [customGoalDraft, setCustomGoalDraft] = useState("");
  const [customGoals, setCustomGoals] = useState(initialGoals.customValues);
  const [nameError, setNameError] = useState<string>();
  const [headlineError, setHeadlineError] = useState<string>();
  const [goalError, setGoalError] = useState<string>();
  const [ageError, setAgeError] = useState<string>();
  const [loading, setLoading] = useState(false);

  // Optional invite code — applied inline so it attributes before onboarding
  // completes (the backend only accepts codes while the account is new).
  const [inviteCode, setInviteCode] = useState("");
  const [inviteApplied, setInviteApplied] = useState(false);
  const [inviteError, setInviteError] = useState<string>();
  const [applyingCode, setApplyingCode] = useState(false);

  async function applyInviteCode() {
    const code = inviteCode.trim();
    if (!code || applyingCode) return;
    setApplyingCode(true);
    setInviteError(undefined);
    try {
      await referralsApi.redeem(code);
      setInviteApplied(true);
    } catch (err) {
      // Surface the server's actual reason (self-referral, already applied,
      // too late in onboarding, etc.) instead of a blanket "invalid code",
      // which hides why a genuine code was refused. Fall back to the generic
      // message only for network/unknown failures with no useful text.
      const message =
        err instanceof ApiError && err.status === 400 && err.message
          ? err.message
          : t(referralStrings.codeInvalid);
      setInviteError(message);
    } finally {
      setApplyingCode(false);
    }
  }

  const seed = useMemo(
    () => name.trim() || user?.avatarSeed || "opncue",
    [name, user?.avatarSeed],
  );
  const ageBandPreview = ageBandFromDateOfBirth(dateOfBirth);
  const maximumDateOfBirth = useMemo(
    () => maximumDateForMinimumAge(MINIMUM_JOIN_AGE),
    [],
  );
  const minimumDateOfBirth = useMemo(
    () => minimumDateForMaximumAge(MAXIMUM_PROFILE_AGE),
    [],
  );
  const selectedFriendshipGoals = normalizeFriendshipGoals(
    friendshipGoals,
    mergeCustomGoals(customGoals, customGoalDraft),
  );
  const displayNameError = validateDisplayName(name);
  const canContinue =
    !displayNameError &&
    headline.trim().length > 0 &&
    dateOfBirth.trim().length === 10 &&
    selectedFriendshipGoals.length > 0;

  async function onContinue() {
    if (!canContinue) return;
    const trimmedName = collapseDisplayName(name);
    const trimmedHeadline = headline.trim();
    const trimmedDob = dateOfBirth.trim();
    const age = ageFromDateOfBirth(trimmedDob);
    const nextFriendshipGoals = normalizeFriendshipGoals(
      friendshipGoals,
      mergeCustomGoals(customGoals, customGoalDraft),
    );

    const nextNameError = validateDisplayName(name);
    if (nextNameError) {
      setNameError(nextNameError);
      return;
    }

    if (!trimmedHeadline) {
      setHeadlineError(t(strings.headlineRequired));
      return;
    }

    if (trimmedHeadline.length > MAX_HEADLINE_LENGTH) {
      setHeadlineError(t(strings.headlineTooLong, { max: MAX_HEADLINE_LENGTH }));
      return;
    }

    if (!nextFriendshipGoals.length) {
      setGoalError(t(strings.goalRequired));
      return;
    }

    if (age === null || age < MINIMUM_JOIN_AGE || age > MAXIMUM_PROFILE_AGE) {
      setAgeError(t(strings.minimumAge));
      return;
    }

    setLoading(true);
    try {
      // `age` is deliberately not sent. It is derived from `dateOfBirth` — by
      // the check above, from the same value — and the server derives it the
      // same way on every read. Sending it stored a second copy that stopped
      // being true on the user's next birthday.
      await setProfile({
        displayName: trimmedName,
        headline: trimmedHeadline,
        dateOfBirth: trimmedDob,
        lookingFor: nextFriendshipGoals,
      });
      router.push("/(onboarding)/permissions");
    } finally {
      setLoading(false);
    }
  }

  function addCustomGoal() {
    const nextCustomGoals = mergeCustomGoals(customGoals, customGoalDraft);

    if (!customGoalDraft.trim()) {
      setGoalError(t(strings.customGoalRequired));
      return;
    }

    if (nextCustomGoals.length === customGoals.length) {
      setGoalError(
        customGoals.length >= MAX_CUSTOM_GOALS
          ? t(strings.customGoalLimit)
          : t(strings.customGoalDuplicate),
      );
      return;
    }

    setCustomGoals(nextCustomGoals);
    setCustomGoalDraft("");
    setGoalError(undefined);
  }

  return (
    <Screen
      scroll
      footer={
        <Button
          label={t(strings.continue)}
          disabled={!canContinue}
          loading={loading}
          onPress={onContinue}
        />
      }
    >
      {/*
        Plain View on purpose. Screen's scroll container is already
        keyboard-aware; a KeyboardAvoidingView here would be nested inside it
        and fight it.
      */}
      <View style={styles.body}>
        <View style={styles.header}>
          <ThemedText variant="title">{t(strings.profileTitle)}</ThemedText>
          <ThemedText variant="body" tone="secondary">
            {t(strings.profileDescription)}
          </ThemedText>
        </View>

        <View style={styles.previewRow}>
          <Avatar
            seed={seed}
            name={name || "🙂"}
            size={64}
          />
          <View style={styles.previewText}>
            <ThemedText variant="subtitle">
              {name.trim() || t(strings.yourName)}
            </ThemedText>
            {selectedFriendshipGoals.length ? (
              <ThemedText variant="caption" tone="secondary">
                {t(strings.hereFor, {
                  goals: selectedFriendshipGoals
                    .map(
                      (goal) =>
                        FRIENDSHIP_GOALS.find((item) => item.value === goal)
                          ?.label ?? goal,
                    )
                    .slice(0, 2)
                    .join(" · "),
                })}
              </ThemedText>
            ) : (
              <ThemedText variant="caption" tone="muted">
                {t(strings.chooseReasonBelow)}
              </ThemedText>
            )}
          </View>
        </View>

        <TextField
          label={t(strings.displayName)}
          value={name}
          onChangeText={(value) => {
            setName(value);
            if (nameError) setNameError(undefined);
          }}
          placeholder={t(strings.displayNamePlaceholder)}
          autoCapitalize="words"
          maxLength={MAX_DISPLAY_NAME_LENGTH}
          hint={`${collapseDisplayName(name).length}/${MAX_DISPLAY_NAME_LENGTH}`}
          error={nameError ?? displayNameError}
          autoFocus
        />

        <Button
          label={t(strings.randomName)}
          variant="secondary"
          size="md"
          fullWidth={false}
          onPress={() => {
            setName(generateDisplayName());
            setNameError(undefined);
          }}
          style={styles.randomNameButton}
        />

        <TextField
          label={t(strings.headline)}
          value={headline}
          onChangeText={(value) => {
            setHeadline(value);
            if (headlineError) setHeadlineError(undefined);
          }}
          placeholder={t(strings.headlinePlaceholder)}
          autoCapitalize="sentences"
          maxLength={MAX_HEADLINE_LENGTH}
          hint={`${headline.length}/${MAX_HEADLINE_LENGTH}`}
          error={headlineError}
        />

        <View style={styles.fieldWrap}>
          <ThemedText variant="label" tone="secondary" style={styles.fieldLabel}>
            {t(strings.dateOfBirth)}
          </ThemedText>
          <DateOfBirthPicker
            value={dateOfBirth}
            onChange={(value) => {
              setDateOfBirth(value);
              if (ageError) setAgeError(undefined);
            }}
            minimumDate={minimumDateOfBirth}
            maximumDate={maximumDateOfBirth}
            placeholder={t(strings.dateOfBirthPlaceholder)}
            inputContainerStyle={[
              styles.dateInput,
              {
                backgroundColor: c.surface,
                borderColor: ageError ? c.danger : c.border,
              },
            ]}
            textStyle={[styles.dateText, { color: c.text }]}
            placeholderTextColor={c.textMuted}
          />
          <ThemedText
            variant="caption"
            tone={ageError ? "danger" : "muted"}
            style={styles.fieldHint}
          >
            {ageError ??
              (ageBandPreview
                ? t(strings.publicAgeBand, { ageBand: ageBandPreview })
                : t(strings.dateOfBirthPrivate))}
          </ThemedText>
        </View>

        <View>
          <ThemedText variant="label" tone="secondary" style={styles.vibeLabel}>
            {t(strings.friendshipGoal)}
          </ThemedText>
          <ThemedText variant="caption" tone="muted" style={styles.vibeHint}>
            {t(strings.chooseOne)}
          </ThemedText>
          <FriendshipGoalSelect
            selectedValue={friendshipGoals[0] ?? ""}
            valueLabel={
              FRIENDSHIP_GOALS.find(
                (goal) => goal.value === friendshipGoals[0],
              )?.label ?? null
            }
            placeholder={t(strings.chooseReason)}
            onSelect={(value) => {
              if (goalError) setGoalError(undefined);
              setFriendshipGoals([value]);
              if (value !== OTHER_GOAL_VALUE) {
                setCustomGoals([]);
                setCustomGoalDraft("");
              }
            }}
          />
          {friendshipGoals.includes(OTHER_GOAL_VALUE) ? (
            <>
              <View style={styles.otherRow}>
                <View style={styles.otherField}>
                  <TextField
                    value={customGoalDraft}
                    onChangeText={(value) => {
                      setCustomGoalDraft(value);
                      if (goalError) setGoalError(undefined);
                    }}
                    placeholder={t(strings.customReasonPlaceholder)}
                    autoCapitalize="sentences"
                    maxLength={40}
                  />
                </View>
                <Button
                  label={t(strings.add)}
                  size="md"
                  fullWidth={false}
                  onPress={addCustomGoal}
                  style={styles.addButton}
                />
              </View>

              {customGoals.length ? (
                <View style={styles.vibeWrap}>
                  {customGoals.map((goal) => (
                    <Pressable
                      key={goal}
                      onPress={() =>
                        setCustomGoals((current) =>
                          current.filter((item) => item !== goal),
                        )
                      }
                      style={[
                        styles.chip,
                        {
                          backgroundColor: c.tint,
                          borderColor: c.tint,
                        },
                      ]}
                    >
                      <ThemedText
                        variant="caption"
                        style={{
                          color: c.onTint,
                          fontWeight: "600",
                        }}
                      >
                        {goal}
                      </ThemedText>
                    </Pressable>
                  ))}
                </View>
              ) : null}
            </>
          ) : null}
          {goalError ? (
            <ThemedText variant="caption" tone="danger" style={styles.fieldHint}>
              {goalError}
            </ThemedText>
          ) : null}
        </View>

        <View style={styles.fieldWrap}>
          <ThemedText variant="label" tone="secondary" style={styles.fieldLabel}>
            {t(referralStrings.haveCode)}
          </ThemedText>
          {inviteApplied ? (
            <View style={styles.inviteAppliedRow}>
              <Ionicons name="checkmark-circle" size={18} color={c.tint} />
              <ThemedText variant="caption" tone="secondary">
                {t(referralStrings.codeApplied)}
              </ThemedText>
            </View>
          ) : (
            <View style={styles.otherRow}>
              <View style={styles.otherField}>
                <TextField
                  value={inviteCode}
                  onChangeText={(value) => {
                    setInviteCode(value);
                    if (inviteError) setInviteError(undefined);
                  }}
                  placeholder={t(referralStrings.codePlaceholder)}
                  autoCapitalize="characters"
                  autoCorrect={false}
                  maxLength={32}
                  error={inviteError}
                />
              </View>
              <Button
                label={t(referralStrings.apply)}
                size="md"
                fullWidth={false}
                onPress={applyInviteCode}
                loading={applyingCode}
                disabled={!inviteCode.trim()}
                style={styles.addButton}
              />
            </View>
          )}
        </View>
      </View>
    </Screen>
  );
}

/**
 * Dropdown for "What brings you here?" — a tap-to-open list of friendship
 * goals, matching the select used in the filter and edit-profile screens.
 */
function FriendshipGoalSelect({
  selectedValue,
  valueLabel,
  placeholder,
  onSelect,
}: {
  selectedValue: string;
  valueLabel: string | null;
  placeholder: string;
  onSelect: (value: string) => void;
}) {
  const c = useTheme();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t(strings.goalSelectionA11y, {
          selection: valueLabel ?? placeholder,
        })}
        accessibilityState={{ expanded: open }}
        onPress={() => setOpen(true)}
        style={[
          styles.dropdown,
          { borderColor: c.border, backgroundColor: c.surface },
        ]}
      >
        <ThemedText
          variant="body"
          numberOfLines={1}
          style={{ flex: 1, color: valueLabel ? c.text : c.textMuted }}
        >
          {valueLabel ?? placeholder}
        </ThemedText>
        <Ionicons name="chevron-down" size={18} color={c.textMuted} />
      </Pressable>

      <Modal
        visible={open}
        transparent
        animationType="fade"
        statusBarTranslucent
        onRequestClose={() => setOpen(false)}
      >
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t(strings.closeGoalOptions)}
          style={styles.dropdownBackdrop}
          onPress={() => setOpen(false)}
        />

        <View style={styles.dropdownCenter} pointerEvents="box-none">
          <View
            style={[
              styles.dropdownSheet,
              { backgroundColor: c.surface, borderColor: c.border },
            ]}
          >
            <View style={styles.dropdownSheetHeader}>
              <View style={styles.dropdownSheetTitleWrap}>
                <ThemedText variant="bodyStrong" style={styles.dropdownSheetTitle}>
                  {t(strings.friendshipGoalShort)}
                </ThemedText>
                <ThemedText variant="caption" tone="muted">
                  {t(strings.chooseOne)}
                </ThemedText>
              </View>

              <Pressable
                accessibilityRole="button"
                accessibilityLabel={t(strings.closeGoalOptions)}
                hitSlop={10}
                onPress={() => setOpen(false)}
                style={[
                  styles.dropdownCloseButton,
                  { backgroundColor: c.backgroundSelected },
                ]}
              >
                <Ionicons name="close" size={18} color={c.textMuted} />
              </Pressable>
            </View>

            <ScrollView
              bounces={false}
              showsVerticalScrollIndicator
              contentContainerStyle={styles.dropdownOptions}
            >
              {FRIENDSHIP_GOALS.map((goal) => {
                const active = goal.value === selectedValue;
                return (
                  <Pressable
                    key={goal.value}
                    accessibilityRole="button"
                    accessibilityState={{ selected: active }}
                    onPress={() => {
                      onSelect(goal.value);
                      setOpen(false);
                    }}
                    style={({ pressed }) => [
                      styles.dropdownOption,
                      (active || pressed) && {
                        backgroundColor: c.backgroundSelected,
                      },
                    ]}
                  >
                    <ThemedText
                      variant="body"
                      style={{
                        flex: 1,
                        color: c.text,
                        fontWeight: active ? "700" : "500",
                      }}
                    >
                      {goal.label}
                    </ThemedText>
                    {active ? (
                      <Ionicons name="checkmark" size={18} color={c.tint} />
                    ) : null}
                  </Pressable>
                );
              })}
            </ScrollView>
          </View>
        </View>
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  body: { flex: 1, gap: Spacing.xl, paddingTop: Spacing.xl },
  header: { gap: Spacing.sm },
  previewRow: { flexDirection: "row", alignItems: "center", gap: Spacing.lg },
  previewText: { gap: 2 },
  vibeLabel: { marginBottom: Spacing.sm, marginLeft: Spacing.xs },
  vibeHint: { marginBottom: Spacing.sm, marginLeft: Spacing.xs },
  fieldWrap: { alignSelf: "stretch", gap: Spacing.xs },
  fieldLabel: { marginLeft: Spacing.xs },
  randomNameButton: { alignSelf: "flex-start" },
  dateInput: {
    minHeight: 52,
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.md,
    borderWidth: 1,
    borderRadius: Radius.md,
  },
  dateText: { fontSize: 16 },
  fieldHint: { marginLeft: Spacing.xs },
  vibeWrap: { flexDirection: "row", flexWrap: "wrap", gap: Spacing.sm },
  otherRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: Spacing.sm,
    marginTop: Spacing.sm,
  },
  otherField: { flex: 1 },
  addButton: { minHeight: 52 },
  inviteAppliedRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.sm,
    paddingVertical: Spacing.sm,
  },
  chip: {
    paddingHorizontal: Spacing.md,
    paddingVertical: Spacing.sm,
    borderRadius: Radius.pill,
    borderWidth: 1,
  },
  dropdown: {
    minHeight: 52,
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.md,
    borderWidth: 1,
    borderRadius: Radius.md,
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.sm,
  },
  dropdownBackdrop: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(8, 15, 26, 0.5)",
  },
  dropdownCenter: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 28,
  },
  dropdownSheet: {
    width: "100%",
    maxWidth: 380,
    maxHeight: "70%",
    padding: 14,
    gap: 10,
    borderRadius: 18,
    borderWidth: 1,
  },
  dropdownSheetHeader: {
    minHeight: 38,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: Spacing.md,
  },
  dropdownSheetTitleWrap: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  dropdownSheetTitle: {
    fontWeight: "800",
  },
  dropdownCloseButton: {
    width: 34,
    height: 34,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 17,
  },
  dropdownOptions: {
    gap: 2,
  },
  dropdownOption: {
    minHeight: 48,
    paddingHorizontal: Spacing.lg,
    paddingVertical: 12,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: Spacing.sm,
    borderRadius: 10,
  },
});
